/*
 * The files of one conversation: the `chat.files` view of the details column (UX X-A4 N13).
 *
 * It answers the question the timeline cannot. A file lives in the message it was sent with, which
 * is right while that message is the thing being read and useless a fortnight later: "the PDF Bea
 * sent before the holiday" is then twenty screens of scrollback away. Here the same files are a
 * list, newest first, and every row is a way back into the conversation at the moment it was sent.
 *
 * Three things make a row worth reading, and all three come out of the one answer rather than out of
 * a second read: what the file is (its thumbnail where the server derived one, its type glyph
 * otherwise), who sent it and when, and the message it hangs on — the permalink, so the line opens
 * the conversation around it instead of only handing the bytes over again.
 *
 * The row itself is the timeline's (`AttachmentRow`): the same Download, the same preview,
 * the same names. A file list with a second set of controls would be a second thing to keep right.
 *
 * Paging is a button and not a scroll listener. The column is 360 px wide and a reader comes to this
 * list with a question, so a page and *Show more files* answer it; an endless list in a narrow column
 * is a way of losing your place.
 *
 * Past ten files the same list is grouped by the month each one was sent in (VISUAL.md §7.7): that is
 * the point at which a reader stops reading the rows and starts looking for a date, and the month is
 * the coarsest thing they can look for. The bound is what keeps a short list one list — a caption over
 * three rows would be a heading over nothing.
 */

import { useEffect, useState, type ReactNode } from "react"
import { errorText } from "../../../core/auth/index.ts"
import { formatDateTime, formatMonth } from "../../../core/i18n/format.ts"
import { inPlaceClick, useModel } from "../../../core/shell/index.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { messageHref, type Addressable } from "../../../routes/links.ts"
import { Button, ListState } from "../../../ui/index.ts"
import { AttachmentRow } from "../attachments/AttachmentRow.tsx"
import { t } from "../i18n/t.ts"
import type { ConversationFile } from "../wire/chat.gen.ts"
import { fetchFilesPage } from "./api.ts"
import "./conversation.css"

/** What the view is showing. The three answers — nothing yet, nothing at all, nothing worked — differ. */
type Phase = "loading" | "ready" | "failed"

interface Answer {
	conv: ConversationId
	phase: Phase
	files: ConversationFile[]
	hasMore: boolean
	error?: string
}

/**
 * A fresh page one in front of the pages the reader had already opened. Rows are ordered by
 * attachment id, newest first, so everything the fresh page covers is the fresh page's to say: a
 * held row inside its range that it no longer lists is a file an edit unlinked, and it leaves with
 * the re-read. `hasMore` does not change, because the oldest row the view holds does not.
 */
function withNewestPage(fresh: ConversationFile[], held: ConversationFile[]): ConversationFile[] {
	const oldestFresh = fresh[fresh.length - 1]?.id ?? 0
	return [...fresh, ...held.filter((file) => file.id < oldestFresh)]
}

/**
 * How long a list stays one run of rows. Up to ten files a reader reads the list; past that they scan
 * it for a date, and the month a file was sent in is the thing they scan by (VISUAL.md §7.7). Below
 * the bound a caption over three rows would be a heading over nothing.
 */
const FLAT_UNTIL = 10

/** One month's files, newest month first, in the order the answer already has them. */
function byMonth(files: ConversationFile[]): { label: string; files: ConversationFile[] }[] {
	const months: { label: string; files: ConversationFile[] }[] = []
	for (const file of files) {
		const label = formatMonth(file.createdAt)
		const last = months.at(-1)
		if (last?.label === label) last.files.push(file)
		else months.push({ label, files: [file] })
	}
	return months
}

/** The registered `detailsViews` id of this view; a request names it, not the component. */
export const FILES_VIEW = "chat.files"

/** The registered view: the details column hands every view the request's parameters. */
export function FilesListView({ params }: { params: Record<string, string | number> }) {
	return <FilesList conv={Number(params["conv"])} />
}

export function FilesList({ conv }: { conv: ConversationId }) {
	// The answer carries the conversation it is about, so a column asked for a second conversation
	// never shows the first one's files while the new read is still running.
	const [answer, setAnswer] = useState<Answer>({
		conv,
		phase: "loading",
		files: [],
		hasMore: false,
	})
	const [loadingMore, setLoadingMore] = useState(false)
	const users = useModel((model) => model.users)
	const meta = useModel((model) => model.conversations[conv]?.meta)
	// What makes the list live without a subscription of its own: the newest attachment id the loaded
	// window of this conversation holds. A file that arrives — somebody else's, or the reader's own —
	// raises it and the list reads the newest page again, which is where that file is; an ordinary
	// message changes nothing here, so a busy channel costs no requests.
	//
	// The newest id and not a count, because the window also grows when the reader scrolls the
	// timeline into the past, and those attachments are older than anything on screen.
	const newestFileId = useModel((model) =>
		(model.messages[conv]?.rows ?? []).reduce(
			(newest, row) =>
				(row.attachments ?? []).reduce((max, file) => Math.max(max, file.id), newest),
			0,
		),
	)

	// No reset to "loading" here: the answer names the conversation it is about, and the render below
	// reads "this answer is about another conversation" as "nothing yet". One state, set once per read.
	//
	// A second read of page one is merged into the head of what the view holds rather than put in its
	// place, so a reader who had pressed *Show more files* keeps those pages when a file arrives; only
	// the first read of a conversation, and a read after a failure, is the whole answer.
	useEffect(() => {
		let live = true
		fetchFilesPage(conv).then(
			(page) => {
				if (!live) return
				setAnswer((before) =>
					before.conv === conv && before.phase === "ready"
						? { ...before, files: withNewestPage(page.files, before.files) }
						: { conv, phase: "ready", files: page.files, hasMore: page.hasMore },
				)
			},
			(err: unknown) => {
				if (live) {
					setAnswer({ conv, phase: "failed", files: [], hasMore: false, error: errorText(err) })
				}
			},
		)
		return () => {
			live = false
		}
	}, [conv, newestFileId])

	/** The page below the oldest row the view holds. The cursor is that row's own id. */
	const loadMore = () => {
		const oldest = answer.files[answer.files.length - 1]
		if (oldest === undefined || loadingMore) return
		setLoadingMore(true)
		fetchFilesPage(conv, oldest.id)
			.then((page) => {
				setAnswer((before) =>
					before.conv === conv
						? { ...before, files: [...before.files, ...page.files], hasMore: page.hasMore }
						: before,
				)
			})
			.catch((err: unknown) => {
				setAnswer((before) =>
					before.conv === conv ? { ...before, phase: "failed", error: errorText(err) } : before,
				)
			})
			.finally(() => setLoadingMore(false))
	}

	if (answer.conv !== conv || answer.phase === "loading") {
		return <ListState.Loading rows={3} />
	}
	if (answer.phase === "failed") {
		return (
			<ListState.Error
				message={answer.error ?? ""}
				retry={() => setAnswer({ ...answer, phase: "loading" })}
			/>
		)
	}
	// No door: a file arrives with a message, so the way to the first one is the composer — which is on
	// screen already, behind this column.
	if (answer.files.length === 0) return <ListState.Empty title={t("chat.files.empty")} />

	const fileRow = (file: ConversationFile) => (
		<li key={file.id}>
			<AttachmentRow
				file={file}
				namedActions
				meta={sentLine(file, users[file.uploaderId]?.displayName, meta)}
			/>
		</li>
	)
	const months = answer.files.length > FLAT_UNTIL ? byMonth(answer.files) : null
	return (
		<div className="tb-files">
			{months === null ? (
				<ul className="tb-attachments__files" aria-label={t("chat.files.title")}>
					{answer.files.map(fileRow)}
				</ul>
			) : (
				months.map((month) => (
					// The key is the run's own first file and not its month: two runs of the same month
					// are possible where an upload's clock and its id disagree, and two <section>s with
					// one key is a list React reconciles wrongly.
					<section key={month.files[0]?.id ?? month.label}>
						<p className="tb-files__month">{month.label}</p>
						<ul className="tb-attachments__files" aria-label={month.label}>
							{month.files.map(fileRow)}
						</ul>
					</section>
				))
			)}
			{answer.hasMore ? (
				<Button
					variant="neutral"
					className="tb-files__more"
					label={t("chat.files.more")}
					disabled={loadingMore}
					onClick={loadMore}
				/>
			) : null}
		</div>
	)
}

/**
 * The line under a file's name: who sent it, when, and the way back to the message. The name comes
 * out of the reader's own directory — a file was sent by somebody in the conversation, so the model
 * knows them — and an account the model has not filled in yet is left unnamed rather than shown as a
 * number. A conversation the model cannot address yet leaves the line as plain text: a link that
 * goes nowhere is worse than a sentence.
 */
function sentLine(
	file: ConversationFile,
	sender: string | undefined,
	meta: Addressable | undefined,
): ReactNode {
	const when = formatDateTime(file.createdAt)
	const said = sender === undefined ? when : t("chat.files.sentBy", { name: sender, when })
	const href = meta === undefined ? null : messageHref(meta, file.seq)
	if (href === null) return said
	return (
		<a className="tb-files__source" href={href} onClick={inPlaceClick(href)}>
			{said}
		</a>
	)
}
